import { primaryFont } from '../theme/typography';
import { toFixedWithNumbers } from './common';
import { formatDate } from './dateFormat';

export interface MeasurementConfig {
  fontSize: string;
  fontFamily: string;
  fontWeight: string | number;
}

export const DEFAULT_MEASUREMENT_CONFIG: MeasurementConfig = {
  fontSize: '14px',
  fontFamily: primaryFont,
  fontWeight: '500'
};

let canvasContext: CanvasRenderingContext2D | null = null;

function getCanvasContext(): CanvasRenderingContext2D | null {
  if (canvasContext) {
    return canvasContext;
  }

  try {
    const canvas = document.createElement('canvas');
    canvasContext = canvas.getContext('2d');
    return canvasContext;
  } catch (error) {
    console.warn('Canvas context creation failed, falling back to DOM measurement:', error);
    return null;
  }
}

function measureTextWidthCanvas(text: string, config: MeasurementConfig): number {
  const context = getCanvasContext();

  if (!context) {
    console.warn('Canvas context not available, returning 0');
    return 0;
  }

  context.font = `${config.fontWeight} ${config.fontSize} ${config.fontFamily}`;

  const metrics = context.measureText(text);
  return metrics.width;
}

function measureTextWidthDOM(text: string, config: MeasurementConfig): number {
  try {
    const element = document.createElement('span');
    element.style.position = 'absolute';
    element.style.visibility = 'hidden';
    element.style.whiteSpace = 'nowrap';
    element.style.fontSize = config.fontSize;
    element.style.fontFamily = config.fontFamily;
    element.style.fontWeight = String(config.fontWeight);
    element.textContent = text;

    document.body.appendChild(element);
    const width = element.offsetWidth;
    document.body.removeChild(element);

    return width;
  } catch (error) {
    console.warn('DOM measurement failed:', error);
    return text.length * 8;
  }
}

export function measureTextWidth(
  text: string,
  config: MeasurementConfig = DEFAULT_MEASUREMENT_CONFIG
): number {
  if (!text || text.length === 0) {
    return 0;
  }

  const textStr = String(text);

  const context = getCanvasContext();
  if (context) {
    return measureTextWidthCanvas(textStr, config);
  }

  return measureTextWidthDOM(textStr, config);
}

export function getFontConfig(element?: HTMLElement): MeasurementConfig {
  if (!element) {
    return DEFAULT_MEASUREMENT_CONFIG;
  }

  try {
    const computedStyle = window.getComputedStyle(element);

    return {
      fontSize: computedStyle.fontSize || DEFAULT_MEASUREMENT_CONFIG.fontSize,
      fontFamily: computedStyle.fontFamily || DEFAULT_MEASUREMENT_CONFIG.fontFamily,
      fontWeight: computedStyle.fontWeight || DEFAULT_MEASUREMENT_CONFIG.fontWeight
    };
  } catch (error) {
    console.warn('Failed to extract font config from element:', error);
    return DEFAULT_MEASUREMENT_CONFIG;
  }
}

export function formatValueForMeasurement(value: any, type?: string): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (type === 'date' || value instanceof Date) {
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return String(value);
    }
  }

  if (type === 'number' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

export function cleanupMeasurementContext(): void {
  canvasContext = null;
}

export interface SampleConfig {
  maxRows: number;
  headers: any[];
  data: any[];
  tableHeaders: any[];
}

export interface WidthConstraints {
  min: number;
  max?: number;
  padding: number;
}

export interface WidthCalculationResult {
  [columnName: string]: number;
}

export interface HeaderConfig {
  name: string;
  label: string;
  width?: number | string;
  enableSorting?: boolean;
  type?: 'date' | 'number' | 'string';
  render?: (value: any, row: any, data: any) => React.ReactNode;
}

export function calculateColumnWidths(
  headers: HeaderConfig[],
  rowContentSamples: { [columnName: string]: string[] },
  measurementConfig: MeasurementConfig = DEFAULT_MEASUREMENT_CONFIG,
  constraints: WidthConstraints = { min: 80, padding: 24 },
  t: any
): WidthCalculationResult {

  const calculatedWidths: WidthCalculationResult = {};

  headers.forEach((header) => {
    const label = t(header.label)
    if (header.width) {
      const numericWidth = typeof header.width === 'string'
        ? parseInt(header.width.replace(/[^\d]/g, ''), 10)
        : header.width;

      calculatedWidths[label] = numericWidth;
      return;
    }

    const headerText = label;
    let headerWidth = measureTextWidth(headerText, measurementConfig);

    if (header.enableSorting) {
      headerWidth += 48;
    }

    let maxRowContentWidth = 0;
    const samples = rowContentSamples[label] || [];

    samples.forEach((content) => {
      const contentWidth = measureTextWidth(content, measurementConfig);
      maxRowContentWidth = Math.max(maxRowContentWidth, contentWidth);
    });
    
    // const isHeaderWidth=(headerWidth < 100 ? headerWidth + 24 : headerWidth >maxRowContentWidth)

    let calculatedWidth = Math.max(headerWidth < 100 ? headerWidth + 24 : headerWidth, maxRowContentWidth);

    const dynamicPadding = constraints.padding 

    calculatedWidth += dynamicPadding;

    if (maxRowContentWidth > 10) {
      calculatedWidth = Math.max(constraints.min, calculatedWidth);
    } else {
      calculatedWidth = Math.max(60, calculatedWidth);
    }

    if (constraints.max) {
      calculatedWidth = Math.min(constraints.max, calculatedWidth);
    }

    calculatedWidths[label] = Math.round(calculatedWidth) > 100 ? Math.round(calculatedWidth) : 100;
  });

  return calculatedWidths
}

const valueMapper = {
  'amount': {
    format: toFixedWithNumbers
  },
  'date': {
    format: formatDate
  },
  'string': {
    format: (value: any) => value
  },
  'toFixed':{
    format:(value:any)=>toFixedWithNumbers(value)
  }
}

function getNestedValue(obj: any, path: any): any {
  const type = path?.type || 'string';
  if (!path?.name || path?.name === '') {
    return '';
  }

  const value = path?.name.split('.').reduce(
    (acc: any, key: any) => (acc && acc[key] !== undefined ? acc[key] : undefined),
    obj
  );

  if (value === 0 || value === '0') {
    return '0';
  }

  return value !== undefined ? valueMapper[type]?.format(value) : '-';
}
export function sampleRowContent(config: SampleConfig): { [columnName: string]: string[] } {
  const { maxRows, headers, data, tableHeaders, t } = config;
  const samples: { [columnName: string]: string[] } = {};

  headers.forEach((header) => {
    samples[t(header.label)] = [];
  });

  if (!data || !Array.isArray(data) || data.length === 0) {
    return samples;
  }

  let sampledCount = 0;
  const maxSamples = maxRows;

  function sampleRows(rows: any[], level: number = 0, /* parentRow?: any */) {
    if (!Array.isArray(rows) || sampledCount >= maxSamples) {
      return;
    }

    const levelHeaders = tableHeaders && tableHeaders[level] && tableHeaders[level].headers
      ? tableHeaders[level].headers
      : headers;

    for (let i = 0; i < rows.length && sampledCount < maxSamples; i++) {
      const row = rows[i];

      // Process all headers in the original headers array
      headers.forEach((header: any) => {
        const columnLabel = t(header.label);
        if (!samples[columnLabel]) {
          return;
        }

        // Check if this header belongs to the current level
        const headerInCurrentLevel = levelHeaders.find((lh: any) =>
          t(lh.label) === columnLabel
        );

        let content: string;

        if (headerInCurrentLevel) {
          // This header belongs to current level - get value from current row
          if (header.render && typeof header.render === 'function') {
            try {
              const rendered = header.render(getNestedValue(row, header), row, data);

              if (typeof rendered === 'string') {
                content = rendered;
              } else if (typeof rendered === 'number') {
                content = String(rendered);
              } else if (rendered && typeof rendered === 'object' && 'props' in rendered) {
                const props = (rendered as any).props;
                if (props && props.children) {
                  content = String(props.children);
                } else {
                  content = String(rendered);
                }
              } else {
                content = String(rendered);
              }
            } catch (error) {
              content = formatValueForMeasurement(getNestedValue(row, header), header.type);
            }
          } else {
            const value = getNestedValue(row, header);
            content = formatValueForMeasurement(value, header.type);
          }
        } else {
          // This header belongs to a parent level - use placeholder
          content = "-";
        }

        samples[columnLabel].push(content);
      });

      sampledCount++;

      // Recurse into nested data
      if (tableHeaders && tableHeaders[level + 1]) {
        const nextRowKey = tableHeaders[level + 1]?.rowKey;
        if (nextRowKey && row[nextRowKey] && Array.isArray(row[nextRowKey])) {
          sampleRows(row[nextRowKey], level + 1, row);
        }
      }
    }
  }

  sampleRows(data, 0);

  return samples;
}

export function getCheckboxColumnWidth(): number {
  return 60;
}